"use client";
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ApiService } from "../../services/AppService";
import { ResponseObject } from "@/app/classes/main";
import { getToken } from "../../services/HolderService";
import { CustomerUpdateRequest } from "../../classes/main";
import toast from "react-hot-toast";
import { trackEvent } from "@/app/utils/branch";
import { usePathname } from "next/navigation";

const fieldSx = {
  width: "100%",
  m: 0,
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    bgcolor: "#fff",
    fontSize: "0.9375rem",
  },
  "& .MuiOutlinedInput-root:hover": {
    bgcolor: "#fff",
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    bgcolor: "#fff",
    boxShadow: `0 0 0 2px ${alpha("#1f7a4d", 0.25)}`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1f7a4d",
    borderWidth: "1px",
  },
  "& .MuiInputLabel-root": {
    fontWeight: 600,
    fontSize: "0.8125rem",
  },
} as const;

interface SignUpProfileProps {
  handleLoginCloseAfterSignUp: () => void;
}

const SignUpProfile: React.FC<SignUpProfileProps> = ({
  handleLoginCloseAfterSignUp,
}) => {
  const [selectedValue, setSelectedValue] = React.useState("");
  const [responseObject, setResponseObject] = useState<
    ResponseObject | undefined
  >(undefined);
  const apiService = useMemo(() => new ApiService(), []);
  const [values, setValues] = useState({
    name: "",
    email: "",
    mobile: "",
    date: "",
    gender: selectedValue ?? "",
  });
  const [saving, setSaving] = useState(false);
  const pathName = usePathname();

  const getCustomerDetails = useCallback(async () => {
    const responseObject: ResponseObject | undefined =
      await apiService.get<ResponseObject>(
        apiService.customerDetails,
        getToken()!
      );
    setResponseObject(responseObject);
  }, [apiService]);

  useEffect(() => {
    getCustomerDetails();
  }, [getCustomerDetails]);

  useEffect(() => {
    setValues((prevValues) => ({
      ...prevValues,
      gender: responseObject?.object?.gender ?? "",
      name: responseObject?.object?.name ?? "",
      email: responseObject?.object?.workEmail ?? "",
      mobile: responseObject?.object?.mobile ?? "",
      date: responseObject?.object?.dob ?? "",
    }));
    setSelectedValue(responseObject?.object?.gender ?? "");
  }, [responseObject]);

  useEffect(() => {
    setValues((prevValues) => ({
      ...prevValues,
      gender: selectedValue,
    }));
  }, [selectedValue]);

  const handleDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleGenderToggle = (
    _event: React.MouseEvent<HTMLElement>,
    next: string | null
  ) => {
    if (next !== null) setSelectedValue(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;

    if (pathName === "/") {
      trackEvent("COMPLETE_REGISTRATION_FROM_HOME", null);
    } else if (pathName === "/cart") {
      trackEvent("COMPLETE_REGISTRATION_FROM_CART", null);
    }

    const customerUpdateRequest = new CustomerUpdateRequest();
    customerUpdateRequest.name = values.name;
    customerUpdateRequest.workEmail = values.email;
    customerUpdateRequest.dob = values.date;
    customerUpdateRequest.gender = values.gender;

    setSaving(true);
    const t = toast.loading("Saving your profile…");
    apiService
      .post(
        apiService.updateCustomerDetails,
        customerUpdateRequest,
        getToken()!
      )
      .then((value) => {
        toast.dismiss(t);
        if (!value) {
          toast.error("Couldn’t save. Please try again.");
          setSaving(false);
          return;
        }
        toast.success(
          (value as { message?: string })?.message ?? "Profile saved",
          { duration: 3500 }
        );
        getCustomerDetails();
        handleLoginCloseAfterSignUp();
        window.location.reload();
      })
      .catch(() => {
        toast.dismiss(t);
        toast.error("Couldn’t save. Please try again.");
        setSaving(false);
      });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      noValidate
      sx={{
        width: "100%",
        maxWidth: 400,
        mx: "auto",
        borderRadius: "20px",
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: `
          0 1px 0 ${alpha("#fff", 0.6)} inset,
          0 24px 48px ${alpha("#0b1710", 0.12)},
          0 0 0 1px ${alpha("#1f7a4d", 0.08)}
        `,
      }}
    >
      {/* Header — distinct from login OTP screen */}
      <Box
        sx={{
          position: "relative",
          px: 2.25,
          pt: 2.5,
          pb: 2.25,
          background:
            "linear-gradient(145deg, #0c1f14 0%, #123524 38%, #1f7a4d 88%, #1a5c3a 100%)",
          color: "#f7faf8",
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(ellipse 90% 80% at 100% 0%, rgba(242, 196, 107, 0.15) 0%, transparent 55%)",
            pointerEvents: "none",
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            position: "relative",
            zIndex: 1,
            display: "block",
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            fontSize: "0.65rem",
            opacity: 0.85,
            mb: 1,
          }}
        >
          Step 2 · Profile
        </Typography>
        <Typography
          component="h2"
          sx={{
            position: "relative",
            zIndex: 1,
            fontWeight: 800,
            fontSize: { xs: "1.35rem", sm: "1.5rem" },
            letterSpacing: "-0.03em",
            lineHeight: 1.2,
            fontFamily: "var(--salontym-font-display, Georgia, serif)",
            mb: 0.75,
          }}
        >
          Tell us about you
        </Typography>
        <Typography
          variant="body2"
          sx={{
            position: "relative",
            zIndex: 1,
            opacity: 0.92,
            lineHeight: 1.5,
            fontSize: "0.8125rem",
            maxWidth: 320,
          }}
        >
          We use this for bookings, reminders, and a safer in-home experience.
        </Typography>
      </Box>

      {/* Form body */}
      <Stack
        spacing={2}
        sx={{
          p: 2,
          bgcolor: alpha("#f3faf6", 0.85),
          borderTop: `1px solid ${alpha("#1f7a4d", 0.1)}`,
        }}
      >
        <TextField
          fullWidth
          margin="none"
          size="small"
          id="signup-name"
          label="Full name"
          required
          name="name"
          autoComplete="name"
          value={values.name}
          onChange={handleDetails}
          sx={fieldSx}
        />

        <TextField
          fullWidth
          margin="none"
          size="small"
          id="signup-mobile"
          label="Mobile number"
          required
          name="mobile"
          type="tel"
          autoComplete="tel"
          value={values.mobile}
          onChange={handleDetails}
          inputProps={{ inputMode: "numeric", maxLength: 10 }}
          sx={fieldSx}
        />

        <TextField
          fullWidth
          margin="none"
          size="small"
          id="signup-email"
          label="Work email"
          name="email"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={handleDetails}
          placeholder="Optional"
          sx={fieldSx}
        />

        <TextField
          fullWidth
          margin="none"
          size="small"
          id="signup-dob"
          label="Date of birth"
          required
          name="date"
          type="date"
          value={values.date}
          onChange={handleDetails}
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />

        <Box>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              fontWeight: 700,
              color: "#123524",
              mb: 1,
              letterSpacing: "0.02em",
            }}
          >
            Gender
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            value={selectedValue}
            onChange={handleGenderToggle}
            aria-label="Gender"
            sx={{
              display: "flex",
              gap: 0.75,
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                border: `1px solid ${alpha("#1f7a4d", 0.22)} !important`,
                borderRadius: "12px !important",
                py: 1,
                px: 1.5,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "#3d4f45",
                bgcolor: "#fff",
                flex: "1 1 calc(33.33% - 6px)",
                minWidth: "5rem",
                "&.Mui-selected": {
                  bgcolor: alpha("#1f7a4d", 0.14),
                  color: "#0b1710",
                  borderColor: `${alpha("#1f7a4d", 0.5)} !important`,
                  fontWeight: 700,
                },
                "&:hover": {
                  bgcolor: alpha("#1f7a4d", 0.07),
                },
              },
            }}
          >
            <ToggleButton value="female" disableRipple>
              Female
            </ToggleButton>
            <ToggleButton value="male" disableRipple>
              Male
            </ToggleButton>
            <ToggleButton value="others" disableRipple>
              Others
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "center",
            color: alpha("#0b1710", 0.45),
            fontSize: "0.7rem",
            lineHeight: 1.45,
          }}
        >
          Encrypted in transit. We never sell your data.
        </Typography>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={saving}
          sx={{
            py: 1.35,
            mt: 0.5,
            borderRadius: "14px",
            textTransform: "none",
            fontWeight: 800,
            fontSize: "1rem",
            letterSpacing: "0.01em",
            background: "linear-gradient(180deg, #1f8f55 0%, #1f7a4d 45%, #123524 100%)",
            boxShadow: `0 4px 16px ${alpha("#1f7a4d", 0.35)}`,
            "&:hover": {
              background:
                "linear-gradient(180deg, #1a7d4a 0%, #1a6b42 50%, #0f2419 100%)",
            },
            "&:disabled": {
              background: alpha("#1f7a4d", 0.4),
              color: "#fff",
            },
          }}
        >
          {saving ? "Saving…" : "Save & continue"}
        </Button>
      </Stack>
    </Box>
  );
};

export default SignUpProfile;
